import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, withCtx as _withCtx, createTextVNode as _createTextVNode } from "vue";
const _hoisted_1 = { class: "flex items-start gap-3" };
const _hoisted_2 = { class: "flex flex-col gap-1" };
const _hoisted_3 = ["id"];
const _hoisted_4 = { class: "text-sm leading-5 text-(--text-text-body) dark:text-(--text-text-white-default)" };
const _hoisted_5 = { class: "flex flex-col gap-5" };
const _hoisted_6 = { class: "flex flex-col gap-3" };
const _hoisted_7 = { class: "text-xs font-semibold uppercase tracking-wide text-(--text-text-body-subtle) dark:text-(--text-text-white-default)" };
const _hoisted_8 = ["aria-label"];
const _hoisted_9 = { class: "flex w-full items-center gap-3" };
const _hoisted_10 = {
    key: 0,
    class: "h-1.5 w-1.5 rounded-full bg-(--background-background-white) dark:bg-(--text-text-black-default)"
};
const _hoisted_11 = { class: "flex min-w-0 flex-1 flex-col gap-0.5" };
const _hoisted_12 = { class: "truncate text-base font-semibold text-(--text-text-heading) dark:text-(--text-text-white-default)" };
const _hoisted_13 = { class: "text-sm text-(--text-text-body)" };
const _hoisted_14 = {
    key: 0,
    class: "rounded-lg bg-(--background-background-error) border border-(--border-border-error-subtle) px-4 py-3 text-sm text-(--text-text-error) flex items-center gap-2"
};
const _hoisted_15 = { class: "flex w-full items-center justify-between" };
import { ref, watch, useId, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Alert, Avatar, Badge, Button, CardSelectable, IconShape, Modal, Pager } from '@eudonet/opal';
import { displayNameOr } from '../../domain/contactDisplayName';
const PAGE_SIZE = 5;
export default /*@__PURE__*/ _defineComponent({
    __name: 'DesignationCandidateModal',
    props: /*@__PURE__*/ _mergeModels({
        items: {},
        icon: {},
        title: {},
        subtitle: {},
        mandatoryHeading: {},
        chooseLabel: {},
        itemSublabel: {},
        selectedBadgeLabel: {},
        confirmLabel: {},
        backLabel: {},
        unknownItemLabel: {},
        testidPrefix: {},
        submitting: { type: Boolean, default: false },
        serverError: { default: null }
    }, {
        "modelValue": { type: Boolean, ...{ default: false } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["back", "confirm"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        /**
         * Generic "designate a successor among the remaining candidates" modal (OP-7428 review): single
         * owner of the chrome that DesignatePrimaryContactModal (shared email, OP-5804) and
         * DesignateHouseholdPrincipalModal (household, OP-7428) previously duplicated at ~85% — header,
         * mandatory-step Alert, CardSelectable radiogroup with its decorative radio indicator, 5-per-page
         * Pager (OP-7366), reset-on-open, inline server error, and footer. The two flow-specific modals
         * are thin wrappers resolving their own i18n labels and mapping their candidate type onto
         * `items`; a fix here (Figma reconciliation, a11y, pagination) reaches every designation flow.
         *
         * Deliberately NOT dismissable (no overlay click, no Esc, no close button — DS "Critical /
         * Choose" modal): the only exits are the secondary back/cancel button (abandon the pending
         * write) and a successful confirmation.
         */
        const props = __props;
        const open = _useModel(__props, "modelValue");
        const emit = __emit;
        const { t } = useI18n();
        const titleId = useId();
        const selectedId = ref(null);
        const currentPage = ref(1);
        // Reset the selection and page each time the modal opens for a fresh designation.
        watch(open, (isOpen) => {
            if (isOpen) {
                selectedId.value = null;
                currentPage.value = 1;
            }
        });
        const totalPages = computed(() => Math.max(1, Math.ceil(props.items.length / PAGE_SIZE)));
        const paginatedItems = computed(() => {
            const start = (currentPage.value - 1) * PAGE_SIZE;
            return props.items.slice(start, start + PAGE_SIZE);
        });
        const radiogroupEl = ref(null);
        // The one tabbable card of the radiogroup: the selected candidate when it sits on the
        // current page, otherwise the first card of the page (fresh open, or the selection lives
        // on another page).
        const tabbableId = computed(() => {
            const items = paginatedItems.value;
            if (items.length === 0)
                return null;
            return items.some((item) => item.id === selectedId.value) ? selectedId.value : items[0].id;
        });
        function onRadiogroupKeydown(event) {
            const delta = event.key === 'ArrowDown' || event.key === 'ArrowRight'
                ? 1
                : event.key === 'ArrowUp' || event.key === 'ArrowLeft'
                    ? -1
                    : 0;
            if (delta === 0)
                return;
            // The arrow is consumed by the radiogroup — don't let the page scroll behind the modal.
            event.preventDefault();
            const items = paginatedItems.value;
            if (items.length === 0)
                return;
            const currentIndex = items.findIndex((item) => item.id === selectedId.value);
            const nextIndex = currentIndex === -1
                ? delta > 0
                    ? 0
                    : items.length - 1
                : (currentIndex + delta + items.length) % items.length;
            selectedId.value = items[nextIndex].id;
            // The page's cards are already rendered (only their tabindex/aria-checked change),
            // so the matching button is focusable synchronously — no nextTick needed.
            const radios = radiogroupEl.value?.querySelectorAll('[role="radio"]');
            radios?.[nextIndex]?.focus();
        }
        function itemName(item) {
            return displayNameOr(item.name, props.unknownItemLabel);
        }
        function handleConfirm() {
            if (selectedId.value)
                emit('confirm', selectedId.value);
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                modelValue: open.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((open).value = $event)),
                size: "sm",
                dismissable: false,
                "close-on-escape": false,
                "show-close-button": false,
                "show-header-border": false,
                "show-footer-border": false,
                "aria-labelledby": _unref(titleId)
            }, {
                header: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                        _createVNode(_unref(IconShape), {
                            size: "md",
                            color: "orange",
                            variant: "outlined",
                            icon: __props.icon
                        }, null, 8 /* PROPS */, ["icon"]),
                        _createElementVNode("div", _hoisted_2, [
                            _createElementVNode("h2", {
                                id: _unref(titleId),
                                class: "text-xl font-bold text-(--text-text-black-default) dark:text-(--text-text-white-default)"
                            }, _toDisplayString(__props.title), 9 /* TEXT, PROPS */, _hoisted_3),
                            _createElementVNode("p", _hoisted_4, _toDisplayString(__props.subtitle), 1 /* TEXT */)
                        ])
                    ])
                ]),
                footer: _withCtx(() => [
                    _createElementVNode("div", _hoisted_15, [
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            label: __props.backLabel,
                            disabled: __props.submitting,
                            onClick: _cache[1] || (_cache[1] = ($event) => (emit('back')))
                        }, null, 8 /* PROPS */, ["label", "disabled"]),
                        _createVNode(_unref(Button), {
                            variant: "primary",
                            "icon-left": "ri-check-line",
                            label: __props.confirmLabel,
                            disabled: !selectedId.value || __props.submitting,
                            loading: __props.submitting,
                            "data-testid": `${__props.testidPrefix}-confirm`,
                            onClick: handleConfirm
                        }, null, 8 /* PROPS */, ["label", "disabled", "loading", "data-testid"])
                    ])
                ]),
                default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_5, [
                        _createVNode(_unref(Alert), {
                            color: "warning",
                            heading: __props.mandatoryHeading
                        }, null, 8 /* PROPS */, ["heading"]),
                        _createElementVNode("div", _hoisted_6, [
                            _createElementVNode("span", _hoisted_7, _toDisplayString(__props.chooseLabel), 1 /* TEXT */),
                            _createElementVNode("div", {
                                ref_key: "radiogroupEl",
                                ref: radiogroupEl,
                                role: "radiogroup",
                                "aria-label": __props.chooseLabel,
                                class: "flex flex-col gap-2",
                                onKeydown: onRadiogroupKeydown
                            }, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(paginatedItems.value, (item) => {
                                    return (_openBlock(), _createBlock(_unref(CardSelectable), {
                                        key: item.id,
                                        selected: selectedId.value === item.id,
                                        role: "radio",
                                        "aria-label": itemName(item),
                                        tabindex: item.id === tabbableId.value ? 0 : -1,
                                        "data-testid": `${__props.testidPrefix}-card-${item.id}`,
                                        class: "!p-3",
                                        onClick: ($event) => (selectedId.value = item.id)
                                    }, {
                                        default: _withCtx(() => [
                                            _createElementVNode("div", _hoisted_9, [
                                                _createElementVNode("span", {
                                                    "aria-hidden": "true",
                                                    class: _normalizeClass([
                                                        'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border',
                                                        selectedId.value === item.id
                                                            ? 'border-(--text-text-black-default) bg-(--text-text-black-default) dark:border-(--background-background-white) dark:bg-(--background-background-white)'
                                                            : 'border-(--border-border-base-medium) bg-(--background-background-white) dark:border-(--colors-border-base) dark:bg-(--colors-background-black-default)',
                                                    ])
                                                }, [
                                                    (selectedId.value === item.id)
                                                        ? (_openBlock(), _createElementBlock("span", _hoisted_10))
                                                        : _createCommentVNode("v-if", true)
                                                ], 2 /* CLASS */),
                                                _createVNode(_unref(Avatar), {
                                                    name: itemName(item),
                                                    size: "sm",
                                                    presentational: "",
                                                    as: "span"
                                                }, null, 8 /* PROPS */, ["name"]),
                                                _createElementVNode("div", _hoisted_11, [
                                                    _createElementVNode("span", _hoisted_12, _toDisplayString(itemName(item)), 1 /* TEXT */),
                                                    _createElementVNode("span", _hoisted_13, _toDisplayString(__props.itemSublabel), 1 /* TEXT */)
                                                ]),
                                                (selectedId.value === item.id)
                                                    ? (_openBlock(), _createBlock(_unref(Badge), {
                                                        key: 0,
                                                        label: __props.selectedBadgeLabel,
                                                        theme: "green",
                                                        size: "sm",
                                                        type: "with-dot",
                                                        "show-close-icon": false
                                                    }, null, 8 /* PROPS */, ["label"]))
                                                    : _createCommentVNode("v-if", true)
                                            ])
                                        ]),
                                        _: 2 /* DYNAMIC */
                                    }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["selected", "aria-label", "tabindex", "data-testid", "onClick"]));
                                }), 128 /* KEYED_FRAGMENT */))
                            ], 40 /* PROPS, NEED_HYDRATION */, _hoisted_8),
                            (totalPages.value > 1)
                                ? (_openBlock(), _createBlock(_unref(Pager), {
                                    key: 0,
                                    "current-page": currentPage.value,
                                    "total-pages": totalPages.value,
                                    "total-items": __props.items.length,
                                    "items-per-page": PAGE_SIZE,
                                    "show-rows-per-page": false,
                                    "show-items-count": false,
                                    "previous-label": _unref(t)('pagination.previous'),
                                    "next-label": _unref(t)('pagination.next'),
                                    "data-testid": `${__props.testidPrefix}-pager`,
                                    onPageChange: _cache[0] || (_cache[0] = ($event) => (currentPage.value = $event))
                                }, null, 8 /* PROPS */, ["current-page", "total-pages", "total-items", "previous-label", "next-label", "data-testid"]))
                                : _createCommentVNode("v-if", true)
                        ]),
                        (__props.serverError)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                                _cache[3] || (_cache[3] = _createElementVNode("i", {
                                    class: "ri-error-warning-line text-base shrink-0",
                                    "aria-hidden": "true"
                                }, null, -1 /* CACHED */)),
                                _createTextVNode(" " + _toDisplayString(__props.serverError), 1 /* TEXT */)
                            ]))
                            : _createCommentVNode("v-if", true)
                    ])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["modelValue", "aria-labelledby"]));
        };
    }
});
